import type { MouseEvent } from 'react';

import { Button } from '../Button';
import { CardSelectButton } from '../CardSelectButton';
import { FromPriceDisplay } from '../PriceDisplay';
import { Badge } from '../Badge';
import { selectOnCardClick } from '../../utils/cardClick';
import {
  ActionRow,
  BadgeRow,
  Content,
  ContentRow,
  Details,
  EditSearchButton,
  Image,
  ImageAction,
  ImageBadge,
  ImageWrapper,
  ItemCardBase,
  Meta,
  OverlayContent,
  OverlayPrice,
  OverlayTitle,
  Placeholder,
  PriceNote,
  PriceRow,
  StatusBadge,
  TextColumn,
  Title,
  TitleMetaGroup,
  UnavailableOverlay,
  UnavailableOverlayDivider,
  UnavailableOverlayText,
} from './ItemCard.styles';
import { resolveCardImageSizing } from '../EntityImage';
import type { ItemCardProps } from './ItemCard.types';

export function ItemCard({
  image,
  imageAspectRatio,
  imageMobileAspectRatio,
  imageOverlay = false,
  title,
  meta,
  price,
  priceNote,
  badges,
  statusBadge,
  imageBadge,
  imageAction,
  action,
  customAction,
  unavailable,
  unavailableLabel,
  editSearchLabel,
  onEditSearch,
  ariaLabel,
  variant = 'default',
  size = 'large',
  textPosition = 'left',
  boldTitle = false,
  onSelect,
  selectButtonLabel,
}: ItemCardProps) {
  const clickable = Boolean(onSelect);
  const isSmall = size === 'small';
  const imageSizing = resolveCardImageSizing(size, imageAspectRatio, imageMobileAspectRatio);

  const unavailableAnnouncement = unavailable ? unavailableLabel ?? statusBadge : undefined;

  const selectLabel = unavailableAnnouncement
    ? `${ariaLabel ?? title} — ${unavailableAnnouncement}`
    : ariaLabel;

  const selectButton = variant === 'default' && onSelect && selectButtonLabel && (
    <Button
      size={isSmall ? 'small' : undefined}
      aria-label={`${selectButtonLabel}, ${ariaLabel ?? title}`}
      onClick={(event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onSelect();
      }}
    >
      {selectButtonLabel}
    </Button>
  );

  const titleContent =
    onSelect && !selectButton ? (
      <CardSelectButton aria-label={selectLabel} onSelect={onSelect}>
        {title}
      </CardSelectButton>
    ) : (
      title
    );

  const actionButton = action && (
    <Button
      size={isSmall ? 'small' : undefined}
      onClick={(event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        action.onClick();
      }}
    >
      {action.label}
    </Button>
  );

  const baseProps = {
    $unavailable: unavailable,
    $clickable: clickable,
    'data-cy': 'item-card',
    onClick: clickable && onSelect ? selectOnCardClick(onSelect) : undefined,
  };

  const unavailableOverlay = unavailable && unavailableLabel && (
    <UnavailableOverlay>
      <UnavailableOverlayText>{unavailableLabel}</UnavailableOverlayText>
      {onEditSearch && editSearchLabel && (
        <>
          <UnavailableOverlayDivider aria-hidden="true" />
          <EditSearchButton
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              onEditSearch();
            }}
          >
            {editSearchLabel}
          </EditSearchButton>
        </>
      )}
    </UnavailableOverlay>
  );

  if (variant === 'overlay' || variant === 'tile') {
    const isTile = variant === 'tile';
    return (
      <ItemCardBase {...baseProps} $tile={isTile}>
        <ImageWrapper
          $aspectRatio={imageAspectRatio}
          $mobileAspectRatio={imageMobileAspectRatio}
          $imageOverlay={imageOverlay}
          $overlayScrim={Boolean(image.src)}
          $tile={isTile}
          $size={size}
        >
          {image.src ? (
            <Image
              src={image.src}
              alt={image.alt}
              sizing={imageSizing.desktop}
              mobileSizing={imageSizing.mobile}
            />
          ) : (
            <Placeholder>{image.placeholderLabel ?? 'No image'}</Placeholder>
          )}
          {unavailableOverlay}
          <OverlayContent $tile={isTile}>
            <OverlayTitle $tile={isTile}>{titleContent}</OverlayTitle>
            {price && (
              <OverlayPrice>
                <FromPriceDisplay
                  amount={price.amount}
                  currencyCode={price.currencyCode}
                  label={price.label}
                  discount={price.discount}
                  locale={price.locale}
                  size="small"
                />
              </OverlayPrice>
            )}
          </OverlayContent>
        </ImageWrapper>
      </ItemCardBase>
    );
  }

  const statusBadgeNode = statusBadge && <StatusBadge tone="neutral">{statusBadge}</StatusBadge>;

  const badgeRow = badges && badges.length > 0 && (
    <BadgeRow>
      {badges.map((badge) => (
        <Badge key={badge} tone="neutral">
          {badge}
        </Badge>
      ))}
    </BadgeRow>
  );

  const priceRow = price && (
    <PriceRow>
      <FromPriceDisplay
        amount={price.amount}
        currencyCode={price.currencyCode}
        label={price.label}
        discount={price.discount}
        locale={price.locale}
        size="base"
      />
    </PriceRow>
  );

  const priceNoteNode = priceNote && <PriceNote tone="success">{priceNote}</PriceNote>;

  const actions = (customAction || actionButton || selectButton) && (
    <>
      {customAction}
      {actionButton}
      {selectButton}
    </>
  );

  return (
    <ItemCardBase {...baseProps}>
      <ImageWrapper
        $aspectRatio={imageAspectRatio}
        $mobileAspectRatio={imageMobileAspectRatio}
        $imageOverlay={imageOverlay}
        $size={size}
      >
        {image.src ? (
          <Image
            src={image.src}
            alt={image.alt}
            sizing={imageSizing.desktop}
            mobileSizing={imageSizing.mobile}
          />
        ) : (
          <Placeholder>{image.placeholderLabel ?? 'No image'}</Placeholder>
        )}
        {imageBadge && <ImageBadge>{imageBadge}</ImageBadge>}
        {imageAction && (
          <ImageAction
            type="button"
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              imageAction.onClick();
            }}
          >
            {imageAction.label}
          </ImageAction>
        )}
        {unavailableOverlay}
      </ImageWrapper>

      <Content $textPosition={textPosition} $size={size}>
        {isSmall ? (
          <>
            {badgeRow}
            <Details>
              <Title $bold={boldTitle} $size={size}>
                {titleContent}
              </Title>
              {meta && <Meta $size={size}>{meta}</Meta>}
            </Details>

            {statusBadgeNode}
            {priceRow}
            {priceNoteNode}

            {actions && <ActionRow>{actions}</ActionRow>}
          </>
        ) : (
          <ContentRow>
            <TextColumn>
              {badgeRow}
              <TitleMetaGroup>
                <Title $bold={boldTitle} $size={size}>
                  {titleContent}
                </Title>
                {meta && <Meta $size={size}>{meta}</Meta>}
              </TitleMetaGroup>

              {statusBadgeNode}
              {priceRow}
              {priceNoteNode}
            </TextColumn>

            {actions}
          </ContentRow>
        )}
      </Content>
    </ItemCardBase>
  );
}
